import { classifyFinanceStep } from "./external-signing";
import type { FinanceStepResponse } from "./types";

/**
 * WAITING FOR THE CHAIN, INSTEAD OF BELIEVING THE POST.
 *
 * =========================================================================
 * WHAT THIS IS FOR
 * =========================================================================
 *
 * Five platform-wallet actions submit work to the blockchain and answer
 * immediately with a `SUBMIT_*` marker. That marker means "written down,
 * queued" — NOT "done". Reporting it as a result told sellers their NFT was
 * listed before it was, told them a listing was cancelled while it could still
 * be bought, and would have said the same about offers.
 *
 * This started as `listing-settlement.ts`, covering two of them. The offer
 * flows needed the identical thing — same channel, same two-beat shape, same
 * failure mode — so the rules became a table instead of becoming a second copy.
 * Adding the sixth is a row here, not a module.
 *
 * =========================================================================
 * WHAT DECIDES, AND IN WHAT ORDER
 * =========================================================================
 *
 *   1. THE SOCKET, on the user's own channel (`user-private-channel-{uuid}` —
 *      the one already open for collection and NFT creation; no new channel
 *      for any of this). The `*_on_blockchain` keys mean it HAPPENED. Their
 *      siblings (`create_listing`, `create_offer`, …) mean it is still in
 *      flight, and only move the UI into a stated waiting state.
 *   2. A POLL of the thing itself, for when the socket is late or was missed —
 *      a reconnect, a backgrounded tab. The server's own view is the other
 *      authority; it never disagrees, it is just slower to ask.
 *
 * THE POST'S TEXT IS NEVER ONE OF THEM. It is the trigger, not the verdict.
 *
 * =========================================================================
 * AND IT NEVER ENDS IN "FAILED"
 * =========================================================================
 *
 * Running out of patience is not evidence. `pending` says the request is in,
 * the chain has not reported, and the page will show the truth on its next
 * load — which is exactly what is true. Calling that a failure is what the
 * place-offer flow used to do, and reloading disproved it every time.
 */

/** Every action that submits chain work and reports back on the user channel. */
export type ChainWorkMode =
  | "list"
  | "cancelListing"
  | "placeOffer"
  | "cancelOffer"
  | "acceptOffer"
  /** An auction's settlement after the owner chooses the winner (features/auctions/settlement). */
  | "settleAuction"
  /** A currency swap (features/wallet/swap). */
  | "swap"
  /** An AI draft's metadata being written (features/ai-nft/aiEvents) — not chain work, the same card. */
  | "aiMetadata";

type ChainWorkSpec = {
  /** The `data.step` a 2xx answers with when it has only QUEUED the work. Null: the POST names no step (a swap). */
  submitStep: string | null;
  /** Socket key meaning the chain leg is under way. Not a result. */
  inFlightKey: string;
  /** Socket key meaning it is done on-chain. The only successful signal. */
  settledKey: string;
  /** Which id both the response and the events use to name this work. */
  idField: "listing_id" | "offer_id" | "auction_id" | "swap_id" | "nft_id";
};

/**
 * THE TABLE. Each row is a contract with the backend, and the strings are the
 * wire format — never normalised, never guessed at. A typo in any of them is
 * invisible until a real socket is in front of it, which is what
 * `chain-settlement.test.ts` is for.
 */
export const CHAIN_WORK: Record<ChainWorkMode, ChainWorkSpec> = {
  list: {
    submitStep: "SUBMIT_LISTING",
    inFlightKey: "create_listing",
    settledKey: "store_listing_on_blockchain",
    idField: "listing_id",
  },
  cancelListing: {
    submitStep: "SUBMIT_CANCEL_LISTING",
    inFlightKey: "cancel_listing",
    settledKey: "listing_cancelled_on_blockchain",
    idField: "listing_id",
  },
  placeOffer: {
    submitStep: "SUBMIT_OFFER",
    inFlightKey: "create_offer",
    settledKey: "store_offer_on_blockchain",
    idField: "offer_id",
  },
  cancelOffer: {
    submitStep: "SUBMIT_CANCEL_OFFER",
    inFlightKey: "cancel_offer",
    settledKey: "offer_cancelled_on_blockchain",
    idField: "offer_id",
  },
  acceptOffer: {
    submitStep: "SUBMIT_ACCEPT_OFFER",
    inFlightKey: "accept_offer",
    settledKey: "offer_accepted_on_blockchain",
    idField: "offer_id",
  },
  // set-winner `{ is_win: true }`: the NFT's transfer and the payment. Its failure key
  // (`auction_settle_failed`) is read by the dock itself — the only row that has one.
  settleAuction: {
    submitStep: "SUBMIT_SET_WINNER",
    inFlightKey: "set_auction_winner",
    settledKey: "auction_settled_on_blockchain",
    idField: "auction_id",
  },
  // A swap: the POST only registers it (pending). `create_swap` and `swap_failed` are read by the
  // dock itself — the registration may open the card, the failure ends it.
  swap: {
    submitStep: null,
    inFlightKey: "swap_processing",
    settledKey: "store_swap_on_blockchain",
    idField: "swap_id",
  },
  // An AI draft's metadata: `POST /ai` names the draft (`nft_id`), `create_ai_nft` says the model is
  // writing, `ai_nft_metadata_ready` that it is done. `ai_nft_metadata_failed` is read by the dock itself.
  aiMetadata: {
    submitStep: null,
    inFlightKey: "create_ai_nft",
    settledKey: "ai_nft_metadata_ready",
    idField: "nft_id",
  },
};

/**
 * Did this POST merely register the request?
 *
 * `true` means the caller must wait for the chain rather than report success.
 * Anything else — a terminal marker from an older shape, or no step at all —
 * keeps the behaviour it had, because those never promised a second beat.
 */
export function isChainWorkQueued(
  response: FinanceStepResponse | null | undefined,
  mode: ChainWorkMode,
): boolean {
  const step = String(response?.data?.step ?? "").toUpperCase();
  return CHAIN_WORK[mode].submitStep !== null && step === CHAIN_WORK[mode].submitStep;
}

/**
 * What the caller should do with this POST: wait, report now, or refuse.
 *
 * `unknown` is kept separate from `settled` deliberately — a marker this client
 * has never seen is not permission to announce anything.
 */
export function classifyChainPost(
  response: FinanceStepResponse | null | undefined,
  mode: ChainWorkMode,
): "queued" | "settled" | "unknown" {
  if (isChainWorkQueued(response, mode)) return "queued";
  const classification = classifyFinanceStep(response?.data?.step);
  if (classification.kind === "unknown") return "unknown";
  return "settled";
}

/** The id to match events against, when the response named one. */
export function readChainWorkId(
  response: FinanceStepResponse | null | undefined,
  mode: ChainWorkMode,
): number | string | null {
  const value = (response?.data as Record<string, unknown> | undefined)?.[CHAIN_WORK[mode].idField];
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.length > 0) return value;
  return null;
}

export type ChainWorkRef = {
  /** `listing_id` or `offer_id`, whichever this mode uses. */
  id?: number | string | null;
  /** The NFT, when the caller knows it. Events carry it too. */
  nftId?: number | string | null;
};

/**
 * Is this message the one this attempt is waiting for?
 *
 * MATCHED ON IDENTITY, NOT JUST ON THE KEY. The channel is per USER, not per
 * NFT or per offer: someone with three offers in flight would otherwise have
 * the first `store_offer_on_blockchain` close all three dialogs.
 *
 * Lenient about WHICH id it recognises — not every emitter fills both fields —
 * but strict about disagreement: an event naming an id this attempt knows, that
 * differs, is NOT ours. An event naming neither is accepted, because the key is
 * then all the server gave us and refusing it would hang the dialog on
 * something that has already happened.
 *
 * `accept_offer` events reach BOTH the NFT's owner and the offer's author. That
 * is fine and needs no special case: both are watching the same offer id, and
 * both should be told it settled.
 */
export function isSettlementFor(
  message: { key?: unknown; data?: unknown } | null | undefined,
  mode: ChainWorkMode,
  expected: ChainWorkRef,
): boolean {
  if (message?.key !== CHAIN_WORK[mode].settledKey) return false;

  const data = (message?.data ?? {}) as Record<string, unknown>;
  const same = (a: unknown, b: unknown) =>
    a != null && b != null && String(a).toLowerCase() === String(b).toLowerCase();
  const disagrees = (a: unknown, b: unknown) => a != null && b != null && !same(a, b);

  if (disagrees(data[CHAIN_WORK[mode].idField], expected.id)) return false;
  if (disagrees(data.nft_id, expected.nftId)) return false;
  return true;
}

/**
 * Which mode an incoming key belongs to, and which of its two beats it is.
 *
 * Built from the table rather than written out again, so a row added above is
 * recognised here without a second edit. `null` for every other key on the
 * channel — likes, follows, NFT and collection creation — which this reader
 * must ignore rather than guess about.
 */
export type ChainWorkBeat = "in_flight" | "settled";

const BEAT_BY_KEY = new Map<string, { mode: ChainWorkMode; beat: ChainWorkBeat }>();
for (const [mode, spec] of Object.entries(CHAIN_WORK) as [ChainWorkMode, ChainWorkSpec][]) {
  BEAT_BY_KEY.set(spec.inFlightKey, { mode, beat: "in_flight" });
  BEAT_BY_KEY.set(spec.settledKey, { mode, beat: "settled" });
}

export function readChainWorkBeat(
  key: unknown,
): { mode: ChainWorkMode; beat: ChainWorkBeat } | null {
  if (typeof key !== "string") return null;
  return BEAT_BY_KEY.get(key) ?? null;
}

/** The ids an event names, in the shape the waiting side already speaks. */
export function readEventChainRef(mode: ChainWorkMode, data: unknown): ChainWorkRef {
  const payload = (data ?? {}) as Record<string, unknown>;
  const id = payload[CHAIN_WORK[mode].idField];
  return {
    id: typeof id === "number" || typeof id === "string" ? id : null,
    nftId:
      typeof payload.nft_id === "number" || typeof payload.nft_id === "string"
        ? payload.nft_id
        : null,
  };
}

/** A card the dock is holding for one attempt, as this matcher needs to see it. */
export type ChainWorkCandidate = ChainWorkRef & {
  mode: ChainWorkMode;
  /** False once it has settled. A finished card never adopts a later event. */
  open: boolean;
};

/**
 * Which held card this event belongs to — its index, or -1 for none.
 *
 * TWO WAYS TO MATCH, IN THIS ORDER, and the order is the whole point.
 *
 *  1. THE SAME ID. A card whose id the event repeats is unambiguously the one,
 *     whatever else is open. Compared as text: the POST answers `offer_id: 9`
 *     and the socket may send `"9"`.
 *  2. AN UNBOUND CARD OF THE SAME MODE. The POST does not always name the id it
 *     created — the first event often does — so a card opened with none adopts
 *     the id from the event that finds it. This is the same rule the NFT and
 *     collection cards have always used, for the same reason.
 *
 * MODE IS NEVER CROSSED. `cancel_offer` cannot adopt the card of an offer being
 * placed, and `offer_accepted_on_blockchain` — which reaches the BUYER as well
 * as the seller — cannot adopt the buyer's own place-offer card. Both parties
 * are told; only the one who pressed Accept is holding an acceptOffer card.
 *
 * A card that names an id the event contradicts is skipped, never repurposed.
 */
export function findChainWorkIndex(
  candidates: readonly ChainWorkCandidate[],
  mode: ChainWorkMode,
  event: ChainWorkRef,
): number {
  const same = (a: unknown, b: unknown) =>
    a != null && b != null && String(a).toLowerCase() === String(b).toLowerCase();
  const disagrees = (a: unknown, b: unknown) => a != null && b != null && !same(a, b);

  const exact = candidates.findIndex(
    (card) => card.mode === mode && card.open && same(card.id, event.id),
  );
  if (exact >= 0) return exact;

  return candidates.findIndex(
    (card) =>
      card.mode === mode &&
      card.open &&
      card.id == null &&
      !disagrees(card.nftId, event.nftId),
  );
}

/** True while THIS mode's chain leg is reported as under way. */
export function isInFlightFor(
  message: { key?: unknown } | null | undefined,
  mode: ChainWorkMode,
): boolean {
  return message?.key === CHAIN_WORK[mode].inFlightKey;
}

export type SettlementResult = "settled" | "pending";

export type WaitForSettlementDeps = {
  /** Attach a realtime listener. MUST return its own unsubscribe. */
  subscribe: (onMessage: (message: { key?: unknown; data?: unknown }) => void) => () => void;
  /** True once the server's own view matches what was asked for. */
  poll: () => Promise<boolean>;
  /** Fired the first time the chain leg is reported in flight. */
  onInFlight?: () => void;
  /** Injectable for tests; production gets real timers. */
  delay?: (ms: number) => Promise<void>;
  /** How long to watch before reporting `pending`. */
  timeoutMs?: number;
  /** How long to give the socket before asking the server at all. */
  firstPollAfterMs?: number;
  /** Gap between polls once polling has started. */
  pollEveryMs?: number;
};

/**
 * Generous, because the alternative is lying. A chain leg can take tens of
 * seconds; reporting `pending` early is honest but unhelpful, so the window
 * covers the ordinary case and the poll covers the rest.
 */
export const DEFAULT_SETTLEMENT_TIMEOUT_MS = 90_000;
export const DEFAULT_FIRST_POLL_AFTER_MS = 8_000;
export const DEFAULT_POLL_EVERY_MS = 6_000;

/**
 * Watch for the chain leg, from both directions at once.
 *
 * THE LISTENER ATTACHES HERE, WHICH IS JUST AFTER THE CALLER'S POST RESOLVED.
 * That leaves a gap, and it is worth being exact about how big: the chain leg
 * only STARTS once the backend has handled the request, so a settlement event
 * cannot realistically land before the response has even been read. What can
 * fall in the gap is the in-flight event, and missing that costs a nuance in
 * the wording, not a verdict. Anything genuinely missed — a reconnect, a
 * backgrounded tab — is what the poll is for.
 */
export async function waitForChainSettlement(
  mode: ChainWorkMode,
  expected: ChainWorkRef,
  deps: WaitForSettlementDeps,
): Promise<SettlementResult> {
  const delay = deps.delay ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)));
  const timeoutMs = deps.timeoutMs ?? DEFAULT_SETTLEMENT_TIMEOUT_MS;
  const firstPollAfterMs = deps.firstPollAfterMs ?? DEFAULT_FIRST_POLL_AFTER_MS;
  const pollEveryMs = deps.pollEveryMs ?? DEFAULT_POLL_EVERY_MS;

  let settled = false;
  let announcedInFlight = false;
  let resolveSocket: (() => void) | null = null;

  const socketArrived = new Promise<void>((resolve) => {
    resolveSocket = resolve;
  });

  const unsubscribe = deps.subscribe((message) => {
    if (!announcedInFlight && isInFlightFor(message, mode)) {
      announcedInFlight = true;
      deps.onInFlight?.();
    }
    if (isSettlementFor(message, mode, expected)) {
      settled = true;
      resolveSocket?.();
    }
  });

  try {
    const deadline = Date.now() + timeoutMs;

    // Nothing is asked of the server during this first window — the socket is
    // the fast path and usually the only one needed.
    await Promise.race([socketArrived, delay(firstPollAfterMs)]);

    while (!settled && Date.now() < deadline) {
      try {
        if (await deps.poll()) return "settled";
      } catch (error) {
        // A failed poll is not a verdict. The socket may still answer, and the
        // next poll may succeed; only the deadline ends this.
        console.error("Chain settlement poll failed:", error);
      }
      if (settled) break;
      await Promise.race([socketArrived, delay(pollEveryMs)]);
    }

    return settled ? "settled" : "pending";
  } finally {
    unsubscribe();
  }
}
